import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { codeRepoAPI } from '../services/api';

function CodeRepoProgressMatrix() {
  const { repoId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [repoData, setRepoData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [progressMatrix, setProgressMatrix] = useState([]);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Access denied. Admin only.');
      navigate('/code-repos');
      return;
    }
    fetchMatrixData();
  }, [repoId, user, navigate]);

  const fetchMatrixData = async () => {
    try {
      setLoading(true);
      const response = await codeRepoAPI.getProgressMatrix(repoId);
      setRepoData(response.data.repo);
      setAssignments(response.data.assignments);
      // Sort students alphabetically by name
      const sortedMatrix = [...response.data.progressMatrix].sort((a, b) => 
        a.studentName.localeCompare(b.studentName)
      );
      setProgressMatrix(sortedMatrix);
      setError('');
    } catch (err) {
      console.error('Error fetching matrix:', err);
      setError(err.response?.data?.message || 'Failed to fetch matrix data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#667eea', fontSize: '16px' }}>Loading matrix data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <div className="error">{error}</div>
          <button 
            onClick={() => navigate(`/code-repos/${repoId}/progress`)}
            className="btn btn-secondary"
          >
            ← Back to Progress
          </button>
        </div>
      </div>
    );
  }

  const completedCells = progressMatrix.reduce((sum, student) =>
    sum + student.assignmentCompletion.filter(ac => ac.completed).length, 0
  );
  const totalCells = progressMatrix.length * assignments.length;
  const overallCompletion = totalCells > 0 
    ? Math.round((completedCells / totalCells) * 100) 
    : 0;

  return (
    <div className="container" style={{ paddingTop: '20px', maxWidth: '100%', background: '#ffffff', minHeight: '100vh', borderRadius: '12px' }}>
      {/* Header */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <button 
              onClick={() => navigate(`/code-repos/${repoId}/progress`)}
              className="btn btn-secondary"
              style={{ marginBottom: '12px', fontSize: '13px', padding: '6px 12px' }}
            >
              ← Back to Progress
            </button>
            <h2 style={{ color: '#667eea', fontSize: '24px', marginBottom: '8px' }}>
              📋 Assignment Completion Matrix
            </h2>
            <p style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
              {repoData?.title} ({repoData?.repoCode})
            </p>
          </div>
          <div style={{
            padding: '12px 20px',
            background: '#f0fdf4',
            borderRadius: '8px',
            border: '1px solid #86efac'
          }}>
            <div style={{ fontSize: '12px', color: '#15803d', marginBottom: '2px' }}>
              Overall Completion
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#22c55e' }}>
              {overallCompletion}%
            </div>
          </div>
        </div>
      </div>
      
      <div className="card" style={{ marginBottom: '20px', padding: '16px' }}>
        <div style={{ display: 'flex', gap: '32px', fontSize: '14px' }}>
          <div>
            <strong>{progressMatrix.length}</strong> {progressMatrix.length === 1 ? 'Student' : 'Students'}
          </div>
          <div>
            <strong>{assignments.length}</strong> {assignments.length === 1 ? 'Assignment' : 'Assignments'}
          </div>
          <div>
            <strong>{completedCells}</strong> / <strong>{totalCells}</strong> cells completed
          </div>
        </div>
      </div>

      {/* Matrix */}
      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        {progressMatrix.length === 0 || assignments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ fontSize: '36px', margin: '0 0 12px 0' }}>📊</p>
            <p style={{ color: '#718096', margin: 0 }}>
              {progressMatrix.length === 0
                ? 'No students enrolled in this repository'
                : 'No assignments in this repository'}
            </p>
          </div>
        ) : (
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px'
          }}>
            <thead>
              <tr style={{ background: '#f7fafc' }}>
                <th style={{
                  padding: '16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#1a202c',
                  borderBottom: '2px solid #e2e8f0',
                  position: 'sticky',
                  left: 0,
                  background: '#f7fafc',
                  zIndex: 10,
                  minWidth: '200px'
                }}>
                  Student
                </th>
                {assignments.map((assignment, index) => (
                  <th
                    key={assignment._id}
                    style={{
                      padding: '16px 12px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#1a202c',
                      borderBottom: '2px solid #e2e8f0',
                      minWidth: '120px',
                      maxWidth: '180px'
                    }}
                  >
                    <div style={{ marginBottom: '4px' }}>
                      #{index + 1}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={assignment.title}
                    >
                      {assignment.title}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#718096',
                      marginTop: '2px'
                    }}>
                      {assignment.assignmentCode}
                    </div>
                  </th>
                ))}
                <th style={{
                  padding: '16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#1a202c',
                  borderBottom: '2px solid #e2e8f0',
                  minWidth: '100px',
                  background: '#f7fafc',
                  position: 'sticky',
                  right: 0
                }}>
                  Progress
                </th>
              </tr>
            </thead>
            <tbody>
              {progressMatrix.map((student, studentIndex) => {
                const completedCount = student.assignmentCompletion.filter(ac => ac.completed).length;
                const studentProgress = assignments.length > 0
                  ? Math.round((completedCount / assignments.length) * 100)
                  : 0;

                return (
                  <tr
                    key={student.studentId}
                    style={{
                      background: studentIndex % 2 === 0 ? '#ffffff' : '#f9fafb',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#edf2f7';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = studentIndex % 2 === 0 ? '#ffffff' : '#f9fafb';
                    }}
                  >
                    <td style={{
                      padding: '16px',
                      borderBottom: '1px solid #e2e8f0',
                      position: 'sticky',
                      left: 0,
                      background: 'inherit',
                      zIndex: 5
                    }}>
                      <div style={{ fontWeight: '600', color: '#1a202c', marginBottom: '2px' }}>
                        {student.studentName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#718096' }}>
                        {student.studentEmail}
                      </div>
                    </td>
                    {student.assignmentCompletion.map((completion) => (
                      <td
                        key={completion.assignmentId}
                        style={{
                          padding: '16px 12px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e2e8f0'
                        }}
                      >
                        {completion.completed ? (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            background: '#d1fae5',
                            color: '#065f46',
                            borderRadius: '50%',
                            fontSize: '16px',
                            fontWeight: '700'
                          }}>
                            ✓
                          </span>
                        ) : (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            background: '#fee2e2',
                            color: '#991b1b',
                            borderRadius: '50%',
                            fontSize: '16px',
                            fontWeight: '700'
                          }}>
                            ✗
                          </span>
                        )}
                      </td>
                    ))}
                    <td style={{
                      padding: '16px',
                      textAlign: 'center',
                      borderBottom: '1px solid #e2e8f0',
                      position: 'sticky',
                      right: 0,
                      background: 'inherit'
                    }}>
                      <div style={{
                        display: 'inline-block',
                        padding: '6px 12px',
                        background: studentProgress === 100
                          ? '#d1fae5'
                          : studentProgress >= 50
                          ? '#dbeafe'
                          : '#fee2e2',
                        color: studentProgress === 100
                          ? '#065f46'
                          : studentProgress >= 50
                          ? '#1e40af'
                          : '#991b1b',
                        borderRadius: '6px',
                        fontWeight: '700',
                        fontSize: '14px'
                      }}>
                        {studentProgress}%
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Legend */}
      <div className="card" style={{ marginTop: '20px', padding: '16px' }}>
        <div style={{ fontSize: '13px', color: '#718096', marginBottom: '12px', fontWeight: '600' }}>
          Legend:
        </div>
        <div style={{ display: 'flex', gap: '24px', fontSize: '13px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              background: '#d1fae5',
              color: '#065f46',
              borderRadius: '50%',
              fontSize: '14px',
              fontWeight: '700'
            }}>
              ✓
            </span>
            <span style={{ color: '#4a5568' }}>Completed</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              background: '#fee2e2',
              color: '#991b1b',
              borderRadius: '50%',
              fontSize: '14px',
              fontWeight: '700'
            }}>
              ✗
            </span>
            <span style={{ color: '#4a5568' }}>Not Completed</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(CodeRepoProgressMatrix);
